/* global axios */
import ApiClient from './ApiClient';

const getTimeOffset = () => -new Date().getTimezoneOffset() / 60;

class ReportsAPI extends ApiClient {
  constructor() {
    super('reports', { accountScoped: true, apiVersion: 'v2' });
  }

  getReports({
    metric,
    from,
    to,
    type = 'account',
    id,
    groupBy,
    businessHours,
  }) {
    return axios.get(`${this.url}`, {
      params: {
        metric,
        since: from,
        until: to,
        type,
        id,
        group_by: groupBy,
        business_hours: businessHours,
        timezone_offset: getTimeOffset(),
      },
    });
  }

  getDrilldown({
    metric,
    bucketTimestamp,
    from,
    to,
    type = 'account',
    id,
    groupBy,
    businessHours,
    page,
    perPage,
    signal,
  }) {
    const requestConfig = {
      params: {
        metric,
        bucket_timestamp: bucketTimestamp,
        since: from,
        until: to,
        type,
        id,
        group_by: groupBy,
        business_hours: businessHours,
        timezone_offset: getTimeOffset(),
        page,
        per_page: perPage,
      },
    };

    if (signal) {
      requestConfig.signal = signal;
    }

    return axios.get(`${this.url}/drilldown`, requestConfig);
  }

  // eslint-disable-next-line default-param-last
  getSummary(since, until, type = 'account', id, groupBy, businessHours) {
    return axios.get(`${this.url}/summary`, {
      params: {
        since,
        until,
        type,
        id,
        group_by: groupBy,
        business_hours: businessHours,
        timezone_offset: getTimeOffset(),
      },
    });
  }

  getConversationMetric(type = 'account', page = 1) {
    return axios.get(`${this.url}/conversations`, {
      params: {
        type,
        page,
      },
    });
  }

  getAgentReports({ from: since, to: until, businessHours }) {
    return axios.get(`${this.url}/agents`, {
      params: { since, until, business_hours: businessHours },
    });
  }

  // ── Reportes de WhatsApp por agente (Jabvox) ──────────────────────────────
  getWhatsappSummary({
    from: since,
    to: until,
    businessHours,
    templateName,
    inboxId,
  } = {}) {
    return axios.get(`${this.url}/whatsapp/summary`, {
      params: {
        from: since,
        to: until,
        business_hours: businessHours,
        template_name: templateName || undefined,
        inbox_id: inboxId || undefined,
      },
    });
  }

  getWhatsappAgents({
    from: since,
    to: until,
    businessHours,
    templateName,
    inboxId,
  } = {}) {
    return axios.get(`${this.url}/whatsapp/agents`, {
      params: {
        from: since,
        to: until,
        business_hours: businessHours,
        template_name: templateName || undefined,
        inbox_id: inboxId || undefined,
      },
    });
  }

  getWhatsappLabelDurations({ from: since, to: until } = {}) {
    return axios.get(`${this.url}/whatsapp/label_durations`, {
      params: { from: since, to: until },
    });
  }

  getWhatsappLabelLeads({ from: since, to: until, label } = {}) {
    return axios.get(`${this.url}/whatsapp/label_leads`, {
      params: { from: since, to: until, label },
    });
  }

  getWhatsappTemplates() {
    return axios.get(`${this.url}/whatsapp/templates`);
  }

  getWhatsappInboxes() {
    return axios.get(`${this.url}/whatsapp/inboxes`);
  }

  // Detalle por agente: mensajes con contacto, estado y error.
  getWhatsappAgentDetail(
    agentId,
    {
      from: since,
      to: until,
      businessHours,
      templateName,
      inboxId,
      msgStatus,
    } = {}
  ) {
    return axios.get(`${this.url}/whatsapp/agents/${agentId}/detail`, {
      params: {
        from: since,
        to: until,
        business_hours: businessHours,
        template_name: templateName || undefined,
        inbox_id: inboxId || undefined,
        msg_status: msgStatus || undefined,
      },
    });
  }

  getWhatsappConversationTimeline(conversationId) {
    return axios.get(
      `${this.url}/whatsapp/conversations/${conversationId}/timeline`
    );
  }

  getConversationsSummaryReports({ from: since, to: until, businessHours }) {
    return axios.get(`${this.url}/conversations_summary`, {
      params: { since, until, business_hours: businessHours },
    });
  }

  getConversationTrafficCSV({ daysBefore = 6 } = {}) {
    return axios.get(`${this.url}/conversation_traffic`, {
      params: { timezone_offset: getTimeOffset(), days_before: daysBefore },
    });
  }

  getLabelReports({ from: since, to: until, businessHours }) {
    return axios.get(`${this.url}/labels`, {
      params: { since, until, business_hours: businessHours },
    });
  }

  getInboxReports({ from: since, to: until, businessHours }) {
    return axios.get(`${this.url}/inboxes`, {
      params: { since, until, business_hours: businessHours },
    });
  }

  getTeamReports({ from: since, to: until, businessHours }) {
    return axios.get(`${this.url}/teams`, {
      params: { since, until, business_hours: businessHours },
    });
  }

  getBotMetrics({ from, to } = {}) {
    return axios.get(`${this.url}/bot_metrics`, {
      params: { since: from, until: to },
    });
  }

  getBotSummary({ from, to, groupBy, businessHours } = {}) {
    return axios.get(`${this.url}/bot_summary`, {
      params: {
        since: from,
        until: to,
        type: 'account',
        group_by: groupBy,
        business_hours: businessHours,
      },
    });
  }
}

export default new ReportsAPI();
